import { LocationType, ProfileTheme } from '@rma/generic/domain/types';
import { StatisticConfig, StatisticConfigOption, StatisticType } from '@rma/generic/util/statistic-config';
import type { ProfileContactAnalytics } from '@rma/profiles/public/domain';
import type { SearchReview } from '@rma/reviews/public/domain';
import type { BedroomsOptionType } from './bedroom-options';
import type { ImportantOptionType } from './important-options';
import { PropertyTypes } from './property-types';
import { WhenToSellOptionType } from './when-to-sell-options';

export interface FindMyAgentRequest {
  propertyType: PropertyTypes;
  bedrooms: BedroomsOptionType;
  locationId: number;
  locationType?: LocationType;
  addressId?: number;
  localityName?: string;
  address?: string;
  importantCriterion?: ImportantOptionType;
  otherImportantReason?: string | null;
  whenToSell?: WhenToSellOptionType;
}

export type FindMyAgentBranding = Exclude<ProfileTheme, 'themeType' | 'backgroundColour' | 'textColour'> & { hasBranding: boolean };

export type AveragePriceExpectationResultType = 'None' | 'Happy' | 'VeryHappy';

export interface FindMyAgentBadges {
  isHighlyRecommended: boolean;
  isTrustedAgent: boolean;
  isAwardWinner: boolean;
}

export interface FindMyAgentResponse {
  agencyName?: string;
  avatarUrl: string;
  branding: FindMyAgentBranding;
  email: string;
  locationStats: LocationStats;
  name: string;
  overallStats: OverallStats;
  phone: string;
  isMobile: boolean;
  profileListViewTrixelUrl: string;
  profileConnectionTrixelUrl: string;
  profileCode: string;
  profileUrl: string;
  rankingAttributions: string[];
  shortName?: string;
  hasMarketReports: boolean;
  badges: FindMyAgentBadges;
  averagePriceExpectationResult: AveragePriceExpectationResultType;
}

export interface OverallStats extends LocationStats {
  averageDaysOnMarket: number | null;
}

export interface LocationStats {
  activeListings: number | null;
  averageSalePrice: number | null;
  averageStarRating: number | null;
  soldListings: number | null;
  totalRecommendations: number | null;
  totalReviewCount: number | null;
  totalSalePrice: number | null;
}

export function isOverallStatsStats(stats: OverallStats | LocationStats): stats is OverallStats {
  return (stats as OverallStats).averageDaysOnMarket !== undefined;
}

export interface Attribution {
  background: RankingAttributionBackgroundColorType;
  text: RankingAttributionTextColorType;
}

export const RankingAttributionBackgroundColor = ['bg-pink-10', 'bg-blue-10', 'bg-teal-10'] as const;
export type RankingAttributionBackgroundColorType = (typeof RankingAttributionBackgroundColor)[number];

export const RankingAttributionTextColor = ['text-pink-90', 'text-blue-90', 'text-teal-90'] as const;
export type RankingAttributionTextColorType = (typeof RankingAttributionTextColor)[number];

export interface Statistic extends Readonly<StatisticConfigOption> {
  value: number | null;
}

const getStatsConfig = (type: StatisticType): Readonly<StatisticConfigOption> => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const config = StatisticConfig.find((c) => c.type === type)!;

  return config;
};

export const mapStats = (stats: OverallStats | LocationStats): Statistic[] => {
  const statistics: Statistic[] = [
    {
      ...getStatsConfig('reviews'),
      value: stats.totalReviewCount,
    },
    {
      ...getStatsConfig('forSale'),
      value: stats.activeListings,
    },
    {
      ...getStatsConfig('sold'),
      value: stats.soldListings,
    },
    {
      ...getStatsConfig('avgPrice'),
      value: stats.averageSalePrice,
    },
    {
      ...getStatsConfig('totalPrice'),
      value: stats.totalSalePrice,
    },
  ];

  if (isOverallStatsStats(stats)) {
    statistics.push({
      ...getStatsConfig('avgOnMarket'),
      value: stats.averageDaysOnMarket,
    });
  }

  return statistics;
};

export interface FindMyAgentResponseVM extends Exclude<FindMyAgentResponse, 'locationStats' | 'overallStats'> {
  attribution: Attribution;
  showAttribution: boolean;
  overallStatistics: Statistic[];
  locationStatistics: Statistic[];
  showActivity: boolean;
  latestReviewsLoading?: boolean;
  latestReviews?: SearchReview[];
  latestReviewsError?: string;
  contactAnalytics: ProfileContactAnalytics;
  propertyType: string;
  localityName?: string;
  locationId: number;
  locationType?: LocationType;
  address?: string;
  addressId?: number;
}

export const OVERALL_LOCATION_ID = 'OVERALL';
